import React from 'react';
import {
    BlockType,
    CollapsibleHeading as CollapsibleHeadingType,
    SagaElement,
    SagaEditor,
    isCollapsibleHeading,
    Colors,
} from '@saga/shared';
import classNames from 'classnames';
import { ReactEditor, useSlateStatic } from 'slate-react';
import Button from '../../../styled/Button';
import { Trash, ChevronDown, ChevronRight } from 'react-feather';
import { Transforms } from 'slate';
import Tooltip from '../../../popover/Tooltip';
import * as Popover from '@radix-ui/react-popover';
import { track } from '@/analytics';
import { useTranslation } from 'react-i18next';
import { WithExplicitFontType } from '@/components/WithExplicitFontType';
import { Editor } from 'slate';
import { Path } from 'slate';

const collapsibleHeadingColorSuggestions = [
    ...Colors.colorSuggestions,
    {
        label: 'Grey',
        value: 'bg-callout-default',
    },
    {
        label: 'Transparent',
        value: 'bg-transparent',
    },
];

function ContextMenu({
    onRemove,
    background,
    onBackgroundChange,
    isCollapsed,
    onToggleCollapse,
}: {
    onRemove: () => void;
    background: string;
    onBackgroundChange(background: string): void;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
}) {
    const [isPopoverOpen, setPopoverOpen] = React.useState(false);
    const { t } = useTranslation();

    return (
        <Popover.Root onOpenChange={setPopoverOpen}>
            <div
                className={classNames(
                    'absolute top-0 right-[-3px] collapsible-heading-hover:flex opacity-100 flex-row',
                    {
                        flex: isPopoverOpen,
                        hidden: !isPopoverOpen,
                    },
                )}
            >
                <Popover.Trigger asChild>
                    <Button.MenuButton aria-label="Collapsible Heading Context Menu" aria-haspopup="true" />
                </Popover.Trigger>
            </div>
            <Popover.Content align="end" className="z-50">
                <WithExplicitFontType type="interface">
                    <div className="p-1.5 overflow-hidden relative shadow-popupSmall max-w-highlight rounded-md bg-white dark:bg-saga-gray-900 dark:border dark:border-saga-gray-800 pointer-events-auto z-50">
                        <div className="flex flex-col w-full space-y-1">
                            <div className="flex flex-row px-1.5 space-x-1 items-center">
                                <p>{t('common.color')}</p>
                                <div className="flex flex-row">
                                    {collapsibleHeadingColorSuggestions.map((currentColor, i) => (
                                        <Tooltip key={i} placement={'top'} content={currentColor.label}>
                                            <div className="p-0.5">
                                                <Button.ColorButton
                                                    color={currentColor.value}
                                                    label={currentColor.label}
                                                    testId={currentColor.label}
                                                    isSelected={currentColor.value === background}
                                                    onMouseDown={() => {
                                                        onBackgroundChange(currentColor.value);
                                                    }}
                                                />
                                            </div>
                                        </Tooltip>
                                    ))}
                                </div>
                            </div>
                            <Button.Plain onClick={onToggleCollapse} onMouseDown={onToggleCollapse}>
                                <Button.SmallPadding>
                                    <div className="flex items-center space-x-2">
                                        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                        <p className="text-sm">
                                            {t(isCollapsed ? 'common.expand' : 'common.collapse')}
                                        </p>
                                    </div>
                                </Button.SmallPadding>
                            </Button.Plain>
                            <Button.Plain onClick={onRemove} onMouseDown={onRemove}>
                                <Button.SmallPadding>
                                    <div className="flex items-center space-x-2">
                                        <Trash size={14} />
                                        <p className="text-sm">{t('common.remove')}</p>
                                    </div>
                                </Button.SmallPadding>
                            </Button.Plain>
                        </div>
                    </div>
                </WithExplicitFontType>
            </Popover.Content>
        </Popover.Root>
    );
}

const getPaddingClass = (firstChild: SagaElement | undefined): string => {
    if (!firstChild) return 'pt-2';

    switch (firstChild.type) {
        case BlockType.HEADING_3:
            return 'pt-3.5';
        case BlockType.HEADING_2:
            return 'pt-4';
        case BlockType.HEADING_1:
            return 'pt-[22px]';
        case BlockType.PARAGRAPH:
            return 'pt-2.5';
        case BlockType.CHECK_LIST_ITEM:
            return 'pt-2';
        case BlockType.NUMBER_LIST_ITEM:
            return 'pt-2';
        case BlockType.BULLET_LIST_ITEM:
            return 'pt-2';
        default:
            return 'pt-2.5';
    }
};

export const collapsibleHeadingPlugin = SagaEditor.Plugins.createBlockPlugin({
    match: isCollapsibleHeading,
    normalizers: [SagaEditor.Normalizers.collapsibleHeadingNormalizer],
    Component({ children, element, attributes }) {
        const { canEdit } = SagaEditor.useEditorContext();
        const editor = useSlateStatic();

        const removeCollapsibleHeading = () => {
            const path = ReactEditor.findPath(editor, element);

            Transforms.unwrapNodes(editor, { at: path });
        };

        const onBackgroundChange = (bg: string) => {
            const path = ReactEditor.findPath(editor, element);

            Transforms.setNodes(editor, { backgroundColor: bg } as Partial<CollapsibleHeadingType>, {
                at: path,
            });
        };

        const toggleCollapse = () => {
            const path = ReactEditor.findPath(editor, element);

            // Store the current selection to restore focus properly after toggling
            const currentSelection = editor.selection;

            // If we're collapsing and the current selection is within children after first child,
            // we need to move selection to the first child
            const isCollapsing = !element.collapsed;

            if (isCollapsing && currentSelection && element.children.length > 1) {
                const firstChildPath = [...path, 0];

                // Check if selection is within content that will be hidden
                const selectionInRemainingContent = element.children.slice(1).some((child, index) => {
                    const childPath = [...path, index + 1];
                    return Path.isDescendant(currentSelection.focus.path, childPath);
                });

                // If selection is in content that will be hidden, move to end of first child
                if (selectionInRemainingContent) {
                    const endOfFirstChild = Editor.end(editor, firstChildPath);
                    Transforms.select(editor, endOfFirstChild);
                }
            }

            Editor.withoutNormalizing(editor, () => {
                // Toggle the collapsed state
                Transforms.setNodes(editor, { collapsed: !element.collapsed } as Partial<CollapsibleHeadingType>, {
                    at: path,
                });
            });

            track('collapsible_heading_toggle', {
                action: element.collapsed ? 'expand' : 'collapse',
            });

            ReactEditor.focus(editor);
        };

        const features = canEdit
            ? {
                  removeCollapsibleHeading,
                  onBackgroundChange,
                  toggleCollapse,
              }
            : undefined;

        const CollapseIcon = element.collapsed ? ChevronRight : ChevronDown;

        const firstChild = element.children[0] as SagaElement;
        const paddingClass = getPaddingClass(firstChild);

        return (
            <div
                {...attributes}
                className="collapsible-heading-wrapper relative w-full"
                data-collapsed={element.collapsed ? 'true' : 'false'}
            >
                <div className="collapsible-heading">
                    <div
                        className={classNames(
                            'my-1.5 rounded w-full py-0 pl-0.5 pr-4 bg-opacity-50 dark:bg-saga-gray-900 flex items-start',
                            element.backgroundColor,
                        )}
                        id={element.id}
                        data-testid={`collapsible-heading-${element.backgroundColor}`}
                    >
                        <div
                            contentEditable={false}
                            className={classNames('flex items-center select-none cursor-pointer', paddingClass)}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Allow toggling even if not editable
                                toggleCollapse();
                            }}
                        >
                            <span className="collapse-icon">
                                <CollapseIcon size={16} />
                            </span>
                        </div>

                        <div className="collapsible-heading-content pl-2.5 flex-grow">{children}</div>

                        {features && (
                            <div className="ml-auto">
                                <ContextMenu
                                    onRemove={features.removeCollapsibleHeading}
                                    background={element.backgroundColor}
                                    onBackgroundChange={features.onBackgroundChange}
                                    isCollapsed={element.collapsed}
                                    onToggleCollapse={features.toggleCollapse}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
});
